import React, { useState } from 'react';
import { FiMail, FiAlertCircle, FiLoader, FiEye, FiEyeOff } from 'react-icons/fi';
import AppModal from '../../common/AppModal';
import services from '../../services';
import { toast } from 'react-toastify';

const DEFAULT_FORM = {
    name: '',
    email: '',
    tenantId: '',
    clientId: '',
    clientSecret: '',
};

const OutlookAuthUser = ({ outlookStatus, onClose, onConnected, editData }) => {
    const isEdit = Boolean(editData && (editData.id || editData._id));
    const [formData, setFormData] = useState(() =>
        isEdit
            ? {
                  name: editData.name || '',
                  email: editData.email || '',
                  tenantId: editData.tenantId || '',
                  clientId: editData.clientId || '',
                  clientSecret: editData.clientSecret || '',
              }
            : DEFAULT_FORM
    );
    const [showSecret, setShowSecret] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const resetAndClose = () => {
        setFormData(DEFAULT_FORM);
        setErrorMessage('');
        setShowSecret(false);
        onClose();
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleConnect = async (e) => {
        e.preventDefault();
        setErrorMessage('');
        setIsSubmitting(true);
        try {
            if (isEdit) {
                await services.outlookUser.UPDATE(editData.id || editData._id, formData);
                toast.success('Outlook account updated successfully!');
            } else {
                await services.outlookUser.CREATE(formData);
                toast.success('Outlook account connected successfully!');
            }
            onConnected && onConnected();
            resetAndClose();
        } catch (error) {
            const message = error?.response?.data?.message || error?.response?.data?.error || `Failed to ${isEdit ? 'update' : 'connect'} Outlook account`;
            toast.error(message);
            setErrorMessage(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const footer = (
        <>
            <button type="button" className="imap-btn-secondary" onClick={resetAndClose}>
                Close
            </button>
            <button type="submit" form="outlook-connect-form" className="imap-btn-primary" disabled={isSubmitting}>
                {isSubmitting ? <FiLoader className="spin-icon" size={14} /> : null}
                {isSubmitting ? ' Saving...' : isEdit ? 'Save Changes' : 'Connect Account'}
            </button>
        </>
    );

    return (
        <AppModal
            show={outlookStatus}
            onClose={resetAndClose}
            icon={<FiMail size={18} color="#fff" />}
            title={isEdit ? 'Edit Outlook Account' : 'Connect Outlook Account'}
            subtitle={isEdit ? 'Update your Microsoft app registration details' : 'Enter your Microsoft app registration details'}
            maxWidth="560px"
            footer={footer}
        >
            {errorMessage && (
                <div className="cred-alert-error">
                    <FiAlertCircle size={15} />
                    {errorMessage}
                </div>
            )}

            <form id="outlook-connect-form" onSubmit={handleConnect}>
                <div className="cred-form-grid">
                    <div className="cred-form-group">
                        <label htmlFor="name" className="cred-form-label">Mailbox Name</label>
                        <input
                            id="name"
                            type="text"
                            name="name"
                            className="cred-form-input"
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder="Sales Mailbox"
                            required
                            autoFocus
                        />
                    </div>
                    <div className="cred-form-group">
                        <label htmlFor="email" className="cred-form-label">Email Address</label>
                        <input
                            id="email"
                            type="email"
                            name="email"
                            className="cred-form-input"
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder="user@yourcompany.onmicrosoft.com"
                            required
                        />
                    </div>
                </div>

                <div className="cred-form-group">
                    <label htmlFor="tenantId" className="cred-form-label">Tenant ID</label>
                    <input
                        id="tenantId"
                        type="text"
                        name="tenantId"
                        className="cred-form-input"
                        value={formData.tenantId}
                        onChange={handleInputChange}
                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                        required
                    />
                </div>

                <div className="cred-form-group">
                    <label htmlFor="clientId" className="cred-form-label">Client ID</label>
                    <input
                        id="clientId"
                        type="text"
                        name="clientId"
                        className="cred-form-input"
                        value={formData.clientId}
                        onChange={handleInputChange}
                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                        required
                    />
                </div>

                <div className="cred-form-group">
                    <label htmlFor="clientSecret" className="cred-form-label">Client Secret</label>
                    <div className="imap-password-field">
                        <input
                            id="clientSecret"
                            type={showSecret ? 'text' : 'password'}
                            name="clientSecret"
                            className="cred-form-input"
                            value={formData.clientSecret}
                            onChange={handleInputChange}
                            placeholder="Client secret value"
                            required
                        />
                        <button
                            type="button"
                            className="imap-password-toggle"
                            onClick={() => setShowSecret((prev) => !prev)}
                            aria-label={showSecret ? 'Hide secret' : 'Show secret'}
                            tabIndex={-1}
                        >
                            {showSecret ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                        </button>
                    </div>
                </div>
            </form>
        </AppModal>
    );
};

export default OutlookAuthUser;
